/**
 * AddressEditFields — the shared form-fields component for editing a
 * residential-address row. Composed by two hosts:
 *
 *   1. `AddressEditCard` (in `address-row.tsx`) — patient-chart inline
 *      edit-in-list pattern, Done/Cancel/Remove action row, save deferred
 *      to the tab-level "Save changes" button.
 *
 *   2. `EditResidentialAddressView` (in `edit-residential-address-view.tsx`)
 *      — appointment-context modal sub-view, Back/Save header, save commits
 *      via PUT /api/Patients/{id} immediately.
 *
 * Both contexts use IDENTICAL field UI: LOCATION NAME, SEARCH LOCATION
 * (with Google Places autocomplete + Enter-Manually toggle), APARTMENT
 * (search mode only), NOTES, isDefault toggle.
 *
 * Mirrors Flutter `create_residential_address_widget.dart`:
 *   - Search mode (`body()`, lines 99-355) renders all 5 fields incl. apartment
 *   - Manual mode (`bodyManualAddress()`, lines 357-555) hides APARTMENT,
 *     replaces the search picker with a free-text ADDRESS field
 *   - Mode toggle clears `address / city / country / latitude / longitude`
 *     (per `EnterManualAddressState` listener at lines 72-78 of the Dart
 *     source)
 *   - Google Places pick populates address (mainText) + lat/lng + city
 *     (terms[n-2]) + country (terms[n-1]) (lines 203-227 of the Dart
 *     source)
 */

import { useEffect, useState } from 'react';
import { MapPin } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { FieldLabel, FIELD_INPUT_CLASS } from '@/patients/components/personal-details/form-primitives';
import { useDebounce } from '@/shared/hooks/use-debounce';
import {
  formatPredictionAddress,
  deriveCityCountryFromTerms,
  useAddressSuggestions,
  type AddressPrediction,
} from '@/shared/services/use-address-suggestions';
import type { AddressFormValues } from './contact-details-schema';

export interface AddressEditFieldsProps {
  row: AddressFormValues;
  readOnly?: boolean;
  onChange: (next: AddressFormValues) => void;
}

export function AddressEditFields({ row, readOnly, onChange }: AddressEditFieldsProps) {
  // Local search-input state; sync to row.address whenever the row changes
  // externally (e.g. parent resets, or autocomplete pick lands).
  const [search, setSearch] = useState(row.address ?? '');
  useEffect(() => {
    setSearch(row.address ?? '');
  }, [row.address]);

  const debounced = useDebounce(search, 300);
  const { data: suggestions } = useAddressSuggestions({
    input: debounced,
    enabled: !row.isManualAddress && !readOnly,
  });
  const [pickerOpen, setPickerOpen] = useState(false);

  const update = (patch: Partial<AddressFormValues>) => onChange({ ...row, ...patch });

  /**
   * Mode toggle. Mirrors Flutter `EnterManualAddressState` listener
   * (`create_residential_address_widget.dart:72-78`): clears
   * address / city / country / latitude / longitude so the user starts
   * fresh in the other mode.
   */
  const toggleManual = () => {
    if (readOnly) return;
    const next = !row.isManualAddress;
    onChange({
      ...row,
      isManualAddress: next,
      address: null,
      city: null,
      country: null,
      latitude: null,
      longitude: null,
    });
    setSearch('');
    setPickerOpen(false);
  };

  const handlePickSuggestion = (pred: AddressPrediction) => {
    const { mainText, city, country } = deriveCityCountryFromTerms(pred);
    update({
      address: mainText || null,
      city: city || null,
      country: country || null,
      // Backend `/api/Master/GetAddress` doesn't return lat/lng on the
      // autocomplete; Flutter relies on a separate place-details call.
      // We leave lat/lng untouched here — home-visit booking does not
      // require coords today.
    });
    setSearch(mainText);
    setPickerOpen(false);
  };

  return (
    <div className="space-y-6">
      {/* LOCATION NAME — required, both modes (Flutter `create_residential_address_widget.dart:110-135` + `:368-393`) */}
      <div>
        <FieldLabel required>Location name</FieldLabel>
        <Input
          value={row.locationName}
          onChange={(e) => update({ locationName: e.target.value })}
          placeholder="e.g. Home"
          maxLength={50}
          className={cn('mt-1', FIELD_INPUT_CLASS)}
          disabled={readOnly}
        />
      </div>

      {/* SEARCH LOCATION (search mode) or ADDRESS (manual mode), required, with mode toggle */}
      <div>
        <div className="flex items-center justify-between">
          <FieldLabel required>
            {row.isManualAddress ? 'Address' : 'Search location'}
          </FieldLabel>
          <button
            type="button"
            onClick={toggleManual}
            className="text-xs font-semibold text-brand-primary hover:underline"
            disabled={readOnly}
          >
            {row.isManualAddress ? 'Search Address' : 'Enter Manually'}
          </button>
        </div>

        {row.isManualAddress ? (
          <Textarea
            value={row.address ?? ''}
            onChange={(e) => update({ address: e.target.value || null })}
            placeholder="Enter address"
            maxLength={1000}
            rows={3}
            className="mt-1 border-transparent bg-grey-100"
            disabled={readOnly}
          />
        ) : (
          <>
            <div className="relative mt-1">
              <Input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPickerOpen(true);
                }}
                onFocus={() => setPickerOpen(true)}
                onBlur={() => setTimeout(() => setPickerOpen(false), 150)}
                placeholder="Enter location"
                disabled={readOnly}
                className={cn(FIELD_INPUT_CLASS, 'pr-10')}
              />
              <MapPin className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-primary pointer-events-none" />
              {pickerOpen && (suggestions?.predictions?.length ?? 0) > 0 ? (
                <ul className="absolute left-0 right-0 top-full mt-1 z-10 max-h-56 overflow-auto scrollbar-hide rounded-md border border-[var(--surface-line)] bg-[var(--surface-read)] shadow-overlay">
                  {(suggestions?.predictions ?? []).map((p) => (
                    <li key={p.place_id ?? p.reference}>
                      <button
                        type="button"
                        onMouseDown={(e) => {
                          // onMouseDown beats onBlur so the picker doesn't
                          // close before the selection registers.
                          e.preventDefault();
                          handlePickSuggestion(p);
                        }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-grey-100"
                      >
                        {formatPredictionAddress(p)}
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
            <p className="mt-2 text-xs text-text-muted">Search by street name</p>
          </>
        )}
      </div>

      {/* APARTMENT — search mode only. Flutter hides this field in manual
          mode (`bodyManualAddress()` doesn't render it) — locked behavior. */}
      {!row.isManualAddress ? (
        <div>
          <FieldLabel>Apartment</FieldLabel>
          <Input
            value={row.apartment ?? ''}
            onChange={(e) => update({ apartment: e.target.value || null })}
            placeholder="Enter apartment number"
            maxLength={20}
            className={cn('mt-1', FIELD_INPUT_CLASS)}
            disabled={readOnly}
          />
        </div>
      ) : null}

      {/* NOTES — both modes */}
      <div>
        <FieldLabel>Notes</FieldLabel>
        <Textarea
          value={row.notes ?? ''}
          onChange={(e) => update({ notes: e.target.value || null })}
          placeholder="Enter additional notes"
          maxLength={1000}
          rows={2}
          className="mt-1 border-transparent bg-grey-100"
          disabled={readOnly}
        />
      </div>

      {/* DEFAULT TOGGLE — both modes */}
      <div className="flex items-center justify-between pt-1">
        <p className="text-sm text-text-primary">
          Make this address default for home visits
        </p>
        <Switch
          checked={row.isDefault}
          onCheckedChange={(checked) => update({ isDefault: checked })}
          disabled={readOnly}
          aria-label="Make this address default for home visits"
        />
      </div>
    </div>
  );
}

/**
 * Save-enable rule mirrors Flutter
 * `create_edit_patient_bloc.dart:676-681`:
 *   `!isBlank(locationName) && !isBlank(address)`
 *
 * Exported so both hosts (inline edit-card "Done", modal "Save") can
 * gate their commit action against the same criterion.
 */
export function isAddressSaveEnabled(row: AddressFormValues): boolean {
  const ln = (row.locationName ?? '').trim();
  const addr = (row.address ?? '').trim();
  return ln.length > 0 && addr.length > 0;
}
